import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ApiError, IconButton, useSnackbar } from '@jupi/ui'
import type { MyAircraft } from '@jupi/shared'
import { useSaveMyAircraft } from '../../api/lessons'
import { useAircraft } from '../../api/queries'

// The aircraft a student can fly, as chips: the favourite highlighted with a star.
export function AircraftChips({ value }: { value: MyAircraft }) {
  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {value.registrations.map((r) => (
        <Chip
          key={r}
          size="small"
          label={r}
          color={r === value.preferred ? 'primary' : 'default'}
          variant={r === value.preferred ? 'filled' : 'outlined'}
          icon={r === value.preferred ? <Icon icon="mdi:star" /> : undefined}
        />
      ))}
    </Stack>
  )
}

// Choose the aircraft you can fly, and star one as your favourite. The
// wizard looks for times in the favourite first and shows the others beside it.
export function MyAircraftDialog({ open, value, onClose }: { open: boolean; value: MyAircraft; onClose: () => void }) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const phone = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const { data: fleet = [] } = useAircraft()
  const save = useSaveMyAircraft()
  const [chosen, setChosen] = useState<string[]>([])
  const [preferred, setPreferred] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setChosen(value.registrations)
    setPreferred(value.preferred)
  }, [open, value])

  const toggle = (registration: string) => {
    const next = chosen.includes(registration) ? chosen.filter((r) => r !== registration) : [...chosen, registration]
    setChosen(next)
    if (preferred && !next.includes(preferred)) setPreferred(null)
  }

  const submit = async () => {
    try {
      await save.mutateAsync({ registrations: chosen, preferred: chosen.length > 1 ? preferred : null })
      showSnackbar(t('lessons.myAircraftSaved'))
      onClose()
    } catch (err) {
      showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" fullScreen={phone}>
      <DialogTitle>{t('lessons.myAircraft')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('lessons.myAircraftHelp')}
        </Typography>
        <List dense disablePadding>
          {fleet.map((a) => {
            const on = chosen.includes(a.registration)
            return (
              <ListItem
                key={a.registration}
                disablePadding
                secondaryAction={
                  on && chosen.length > 1 ? (
                    <IconButton
                      size="small"
                      aria-label={t('lessons.makeFavourite')}
                      aria-pressed={preferred === a.registration}
                      onClick={() => setPreferred(preferred === a.registration ? null : a.registration)}
                    >
                      <Icon icon={preferred === a.registration ? 'mdi:star' : 'mdi:star-outline'} />
                    </IconButton>
                  ) : null
                }
              >
                <ListItemButton onClick={() => toggle(a.registration)}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox edge="start" checked={on} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText primary={a.registration} secondary={a.aircraftType} />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
        {chosen.length > 1 && !preferred ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            <Box>{t('lessons.pickFavourite')}</Box>
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={save.isPending}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
